/**
 * AutoFit Settings Module
 *
 * Defines per-node autofit behavior with sensible per-type defaults.
 * Four independent flags control sizing:
 *
 * - autoFitWidth: auto-expand width to fit content
 * - autoFitHeight: auto-expand height to fit content
 * - autoSizeText: scale font to fill box (mutually exclusive with autoFit*)
 * - wordWrap: wrap text at node boundary
 */

export interface AutoFitSettings {
	autoFitWidth: boolean;
	autoFitHeight: boolean;
	autoSizeText: boolean;
	wordWrap: boolean;
}

/** Fallback defaults for unknown node types — both axes on for backward compat */
export const GLOBAL_DEFAULTS: AutoFitSettings = {
	autoFitWidth: true,
	autoFitHeight: true,
	autoSizeText: false,
	wordWrap: true,
};

/** Per-type defaults */
export const TYPE_DEFAULTS: Record<string, AutoFitSettings> = {
	markdown: { autoFitWidth: true, autoFitHeight: true, autoSizeText: false, wordWrap: true },
	'sticky-note': { autoFitWidth: false, autoFitHeight: false, autoSizeText: true, wordWrap: true },
	header: { autoFitWidth: true, autoFitHeight: true, autoSizeText: false, wordWrap: false },
};

const FLAG_KEYS: (keyof AutoFitSettings)[] = [
	'autoFitWidth',
	'autoFitHeight',
	'autoSizeText',
	'wordWrap',
];

/**
 * Resolve autofit settings for a node by merging explicit flags over type defaults.
 * Explicit flags live in `node.data.obj ?? node.data`.
 */
export function getAutoFitSettings(node: {
	type?: string;
	data?: Record<string, unknown>;
}): AutoFitSettings {
	const defaults = TYPE_DEFAULTS[node.type ?? ''] ?? GLOBAL_DEFAULTS;
	const obj = (node.data?.obj as Record<string, unknown>) ?? node.data ?? {};

	const result = { ...defaults };
	for (const key of FLAG_KEYS) {
		if (typeof obj[key] === 'boolean') {
			result[key] = obj[key] as boolean;
		}
	}
	return result;
}

/**
 * Set a single autofit flag on node data, enforcing mutual exclusivity:
 * - Turning on autoSizeText turns off autoFitWidth and autoFitHeight
 * - Turning on autoFitWidth or autoFitHeight turns off autoSizeText
 *
 * Mutates `data` in place and returns the updated flags.
 */
export function setAutoFitFlag(
	data: Record<string, unknown>,
	flag: keyof AutoFitSettings,
	value: boolean,
): void {
	data[flag] = value;

	if (value) {
		if (flag === 'autoSizeText') {
			data.autoFitWidth = false;
			data.autoFitHeight = false;
		} else if (flag === 'autoFitWidth' || flag === 'autoFitHeight') {
			data.autoSizeText = false;
		}
	}
}
